import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { useApp } from "../context/AppContext";
import { Card } from "./ui";
import { formatMoney } from "../lib/utils";
import { PieChart as PieIcon } from "lucide-react";

const CATS = [
  { key: "stocks", color: "#6366F1", label: ["Actions", "Stocks"] },
  { key: "etf", color: "#0EA5E9", label: ["ETF", "ETFs"] },
  { key: "fund", color: "#F472B6", label: ["Fonds", "Funds"] },
  { key: "bond", color: "#2DD4BF", label: ["Obligations", "Bonds"] },
  { key: "option", color: "#FB923C", label: ["Options", "Options"] },
  { key: "cash", color: "#10B981", label: ["Liquidités", "Cash"] },
  { key: "property", color: "#A3B18A", label: ["Immobilier", "Property"] },
  { key: "vehicle", color: "#60A5FA", label: ["Véhicules", "Vehicles"] },
  { key: "collectible", color: "#C084FC", label: ["Collection", "Collectibles"] },
  { key: "precious_metal", color: "#F59E0B", label: ["Métaux précieux", "Precious Metals"] },
  { key: "other", color: "#94A3B8", label: ["Autres", "Other"] },
];

const SELECTABLE = new Set(["stocks", "etf", "fund", "bond", "option"]);

export default function AllocationDonut({ allocation, base, hidden, onSelect, activeKey }) {
  const { t, lang } = useApp();
  const money = (v, dec = 0) => (hidden ? "••••" : formatMoney(v, base, { decimals: dec }));
  const a = allocation || {};

  const slices = CATS
    .map((c) => ({ ...c, value: Math.max(0, Number(a[c.key] || 0)) }))
    .filter((c) => c.value > 0.5);
  const gross = slices.reduce((s, c) => s + c.value, 0);
  const liabilities = Math.max(0, Number(a.liabilities || 0));
  const netWorth = gross - liabilities;
  const pick = (key) => { if (onSelect && SELECTABLE.has(key)) onSelect(activeKey === key ? null : key); };

  return (
    <Card data-testid="allocation-donut">
      <div className="flex items-center gap-2 mb-3">
        <PieIcon className="w-4 h-4 text-text-secondary" />
        <h3 className="font-heading font-semibold">{t("alloc_title")}</h3>
      </div>

      {gross < 0.5 ? (
        <p className="text-sm text-text-secondary py-6 text-center">{t("alloc_empty")}</p>
      ) : (
        <div className="flex flex-col sm:flex-row items-center gap-5">
          <div className="relative shrink-0" style={{ width: 168, height: 168 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={slices} dataKey="value" nameKey="key" cx="50%" cy="50%"
                  innerRadius={56} outerRadius={80} paddingAngle={2} stroke="none"
                  isAnimationActive={false} onClick={(d) => pick(d?.key || d?.payload?.key)}>
                  {slices.map((c) => (
                    <Cell key={c.key} fill={c.color}
                      cursor={SELECTABLE.has(c.key) && onSelect ? "pointer" : "default"}
                      fillOpacity={activeKey && activeKey !== c.key ? 0.3 : 1} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-[10px] uppercase tracking-wide text-text-secondary">{t("alloc_net_worth")}</span>
              <span className="text-base font-heading font-semibold tabular-nums" data-testid="alloc-net-worth">{money(netWorth)}</span>
            </div>
          </div>

          <div className="flex-1 w-full space-y-1.5">
            {slices.map((c) => {
              const selectable = SELECTABLE.has(c.key) && onSelect;
              const active = activeKey === c.key;
              return (
                <div key={c.key} role={selectable ? "button" : undefined}
                  tabIndex={selectable ? 0 : undefined}
                  onClick={() => pick(c.key)}
                  onKeyDown={selectable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick(c.key); } } : undefined}
                  className={`flex items-center gap-2 text-sm rounded-md px-1 -mx-1 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/50 ${selectable ? "cursor-pointer hover:bg-surface-hover" : ""} ${active ? "bg-surface-hover" : ""}`}
                  style={activeKey && !active ? { opacity: 0.5 } : undefined}
                  data-testid={`alloc-row-${c.key}`}>
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: c.color }} />
                  <span className="flex-1 text-text-secondary truncate">{c.label[lang === "fr" ? 0 : 1]}</span>
                  <span className="tabular-nums font-medium">{money(c.value)}</span>
                  <span className="tabular-nums text-[11px] text-text-secondary w-10 text-right">{Math.round((c.value / gross) * 100)}%</span>
                </div>
              );
            })}
            {liabilities > 0.5 && (
              <div className="flex items-center gap-2 text-sm pt-1.5 mt-1.5 border-t" data-testid="alloc-row-liabilities">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: "#EF4444" }} />
                <span className="flex-1 text-text-secondary truncate">{t("alloc_liabilities")}</span>
                <span className="tabular-nums font-medium" style={{ color: "#EF4444" }}>−{money(liabilities)}</span>
                <span className="w-10" />
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
